/**
 * File: articles.ts
 * Description: Imperative API calls for creating and deleting admin FAQ articles. Plain async
 *                functions called on demand from event handlers.
 */
import axios from 'axios';
import { ENDPOINT_ADMIN_ARTICLE } from '@/constants/endpoints_urls';

/**
 * Shape of the JSON body the backend sends on a failed request.
 *
 * @property {string} error - Human-readable message describing why the request failed.
 */
interface ApiErrorResponse {
  error: string;
}

/**
 * Creates a new article via the API, picking the endpoint based on whether the article
 * targets an existing category or a new one.
 *
 * @param {number | null} categoryId - Id of the existing category, or null to create a new category with categoryTitle.
 * @param {string} categoryTitle - Title of the category; only sent to the API when categoryId is null.
 * @param {string} articleTitle - Title of the new article.
 * @param {string} articleContent - Markdown content of the new article's answer.
 * @returns {Promise<void>}
 * @throws {Error} When the request fails, with the backend's `error` message when available — callers must handle/surface the rejection.
 */
export async function createArticle(
  categoryId: number | null,
  categoryTitle: string,
  articleTitle: string,
  articleContent: string,
): Promise<void> {
  try {
    if (categoryId) {
      await axios.post(`${ENDPOINT_ADMIN_ARTICLE}/${String(categoryId)}`, {
        articleTitle,
        articleContent,
      });
    } else {
      await axios.post(ENDPOINT_ADMIN_ARTICLE, {
        categoryTitle,
        articleTitle,
        articleContent,
      });
    }
  } catch (error) {
    if (axios.isAxiosError<ApiErrorResponse>(error)) {
      throw new Error(error.response?.data.error ?? error.message, { cause: error });
    }
    throw error;
  }
}

/**
 * Deletes an article via the API.
 *
 * @param {number} categoryId - Id of the category the article belongs to.
 * @param {number} articleId - Id of the article to delete.
 * @returns {Promise<void>}
 * @throws {Error} When the request fails, with the backend's `error` message when available — callers must handle/surface the rejection.
 */
export async function deleteArticle(categoryId: number, articleId: number): Promise<void> {
  try {
    await axios.delete(`${ENDPOINT_ADMIN_ARTICLE}/${String(categoryId)}/${String(articleId)}`);
  } catch (error) {
    if (axios.isAxiosError<ApiErrorResponse>(error)) {
      throw new Error(error.response?.data.error ?? error.message, { cause: error });
    }
    throw error;
  }
}
